/**
 * Historical seed data generators — 12 months of synthetic operational
 * history for Watchmen Command. Powers AEGIS predictions and analytics.
 *
 * Volume (tenantId: 'watchmen-demo'):
 *   Historical loads:        1,800  (150/month × 12)
 *   Historical exceptions:     300  (25/month × 12)
 *   Inventory demand:        7,300  (20 SKUs × 365 days)
 *   Check calls:               500
 *   WMS receiving:           ~1,460 (3-5/day × 365)
 *   WMS putaway:             ~1,460 (1:1 with receiving)
 *   WMS picks:               ~5,850 (50-80/day × last 90 days)
 *   WMS labor productivity:  ~1,095 (3 shifts × 365)
 *
 * All records include universal fields and tenantId: 'watchmen-demo'.
 * Deterministic: a seeded PRNG makes runs reproducible.
 */

const T = 'watchmen-demo';

// ── Deterministic PRNG (mulberry32) ─────────────────────────────────
function prng(seed) {
  let s = seed >>> 0;
  return function rnd() {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function makeHelpers(rnd) {
  const pick = (arr) => arr[Math.floor(rnd() * arr.length)];
  const pickW = (choices) => {
    // choices: [[value, weight], ...]
    const total = choices.reduce((a, [, w]) => a + w, 0);
    let t = rnd() * total;
    for (const [v, w] of choices) { t -= w; if (t <= 0) return v; }
    return choices[choices.length - 1][0];
  };
  const randBetween = (a, b) => Math.floor(rnd() * (b - a + 1)) + a;
  const randFloat = (a, b, dec = 2) => parseFloat((rnd() * (b - a) + a).toFixed(dec));
  const pad = (n, len = 5) => String(n).padStart(len, '0');
  return { pick, pickW, randBetween, randFloat, pad, rnd };
}

// ── Universal fields ────────────────────────────────────────────────
function uf(overrides = {}, createdAt) {
  const created = createdAt || new Date().toISOString();
  return {
    tenantId: T,
    customerId: overrides.customerId ?? null,
    loadId: overrides.loadId ?? null,
    quoteId: overrides.quoteId ?? null,
    createdAt: created,
    createdBy: 'seed',
    updatedAt: overrides.updatedAt || created,
    updatedBy: 'seed',
    deletedAt: null,
    deletedBy: null,
    isDeleted: false,
    version: 1,
    source: 'historical_seed',
    ...overrides,
  };
}

// ── Constants ───────────────────────────────────────────────────────
const CARRIERS = [
  // [scac, name, onTimeRate, answerRate]
  ['SWFT', 'Swift Transportation',   0.88, 0.92],
  ['WERN', 'Werner Enterprises',     0.91, 0.95],
  ['JBHT', 'J.B. Hunt Transport',    0.94, 0.97],
  ['SNDR', 'Schneider National',     0.93, 0.96],
  ['HTLD', 'Heartland Express',      0.95, 0.98],
  ['USAT', 'USA Truck',              0.82, 0.85],
  ['PAMT', 'PAM Transport',          0.86, 0.88],
  ['KNX',  'Knight-Swift',           0.90, 0.93],
  ['CVTI', 'Covenant Transport',     0.85, 0.87],
  ['CRST', 'CRST International',     0.89, 0.91],
  ['MESL', 'Maersk Line',            0.83, 0.78],
  ['MAEU', 'Maersk Sealand',         0.81, 0.76],
  ['CHRW', 'C.H. Robinson',          0.92, 0.94],
  ['XPOL', 'XPO Logistics',          0.87, 0.89],
  ['ODFL', 'Old Dominion',           0.96, 0.97],
  ['FXFE', 'FedEx Freight',          0.93, 0.95],
  ['UPSZ', 'UPS Freight',            0.91, 0.94],
  ['SAIA', 'Saia LTL',               0.88, 0.90],
  ['ESTB', 'Estes Express',          0.86, 0.89],
  ['YRCW', 'YRC Worldwide',          0.72, 0.70], // chronic underperformer
];

const LANES = [
  // top 8 lanes = 70% of volume
  { from: 'Chicago, IL',      to: 'Dallas, TX',       weight: 14, miles: 925 },
  { from: 'Los Angeles, CA',  to: 'Phoenix, AZ',      weight: 12, miles: 372 },
  { from: 'Atlanta, GA',      to: 'Charlotte, NC',    weight: 10, miles: 245 },
  { from: 'Newark, NJ',       to: 'Columbus, OH',     weight:  9, miles: 525 },
  { from: 'Memphis, TN',      to: 'Nashville, TN',    weight:  8, miles: 210 },
  { from: 'Houston, TX',      to: 'New Orleans, LA',  weight:  7, miles: 348 },
  { from: 'Kansas City, MO',  to: 'Denver, CO',       weight:  6, miles: 604 },
  { from: 'Seattle, WA',      to: 'Portland, OR',     weight:  4, miles: 174 },
  // remaining 30% — one-off lanes
  { from: 'Miami, FL',        to: 'Orlando, FL',      weight:  3, miles: 235 },
  { from: 'Boston, MA',       to: 'Philadelphia, PA', weight:  3, miles: 307 },
  { from: 'Detroit, MI',      to: 'Indianapolis, IN', weight:  3, miles: 288 },
  { from: 'Minneapolis, MN',  to: 'Milwaukee, WI',    weight:  2, miles: 337 },
  { from: 'San Francisco, CA',to: 'Sacramento, CA',   weight:  2, miles: 87  },
  { from: 'Tampa, FL',        to: 'Jacksonville, FL', weight:  2, miles: 200 },
  { from: 'Pittsburgh, PA',   to: 'Cleveland, OH',    weight:  2, miles: 135 },
  { from: 'St. Louis, MO',    to: 'Louisville, KY',   weight:  2, miles: 260 },
  { from: 'Salt Lake City, UT', to: 'Las Vegas, NV',  weight:  2, miles: 420 },
];

const MODE_WEIGHTS = [
  ['OTR',         40],
  ['LTL',         20],
  ['Drayage',     15],
  ['Ocean',       10],
  ['Intermodal',   8],
  ['Air',          4],
  ['Parcel',       3],
];

const CUSTOMER_IDS = Array.from({ length: 25 }, (_, i) => `cust-${String(i + 1).padStart(5, '0')}`);

// ── Date helpers ────────────────────────────────────────────────────
function dateDaysAgo(d) {
  const dt = new Date();
  dt.setHours(12, 0, 0, 0);
  dt.setDate(dt.getDate() - d);
  return dt;
}

function isoDaysAgo(d) {
  return dateDaysAgo(d).toISOString();
}

function addHours(iso, h) {
  return new Date(new Date(iso).getTime() + h * 3600000).toISOString();
}

// Seasonal multiplier (1.0 baseline) — summer peaks, Nov-Dec uptick
function seasonalMultiplier(date, category) {
  const m = date.getMonth(); // 0-11
  if (category === 'seafood') {
    // Peaks Jun-Aug (months 5-7)
    return [0.7, 0.7, 0.8, 0.9, 1.1, 1.35, 1.5, 1.45, 1.1, 0.9, 0.8, 0.75][m];
  }
  if (category === 'beef') {
    // Peaks Nov-Dec (10-11)
    return [0.9, 0.85, 0.9, 0.95, 1.0, 1.05, 1.0, 1.0, 1.05, 1.1, 1.35, 1.5][m];
  }
  if (category === 'poultry') {
    // Steady, tiny Q4 bump
    return [0.95, 0.95, 1.0, 1.0, 1.0, 1.0, 1.0, 1.0, 1.05, 1.05, 1.1, 1.15][m];
  }
  if (category === 'produce') {
    // Peaks summer
    return [0.8, 0.8, 0.9, 1.0, 1.1, 1.25, 1.35, 1.35, 1.1, 1.0, 0.85, 0.8][m];
  }
  return 1.0;
}

// Weekly multiplier — higher Mon-Wed (1-3), lower Thu-Fri (4-5), weekend lowest
function weeklyMultiplier(date) {
  const d = date.getDay(); // 0=Sun, 1=Mon...
  return [0.45, 1.15, 1.20, 1.18, 0.95, 0.80, 0.50][d];
}

// Quarterly rate drift (2-3% per quarter) + seasonal spikes
function rateMultiplier(date) {
  const monthsBack = (new Date().getFullYear() - date.getFullYear()) * 12
    + (new Date().getMonth() - date.getMonth());
  // Oldest data: 0.93x. Most recent: 1.00x. ~3% per quarter.
  const quarterDrift = 1.0 - (monthsBack / 12) * 0.07;
  // Seasonal spike: Jun-Aug +5%, Nov-Dec +4%
  const m = date.getMonth();
  let spike = 1.0;
  if (m >= 5 && m <= 7) spike = 1.05;
  else if (m >= 10) spike = 1.04;
  return quarterDrift * spike;
}

// ═══════════════════════════════════════════════════════════════════
// HISTORICAL LOADS (1,800 records)
// ═══════════════════════════════════════════════════════════════════
export function generateHistoricalLoads(seed = 18001) {
  const { pick, pickW, randBetween, randFloat, pad, rnd } = makeHelpers(prng(seed));
  const out = [];
  let idCounter = 100000;

  for (let m = 0; m < 12; m++) {
    // monthsBack = 11 (oldest) down to 0 (this month)
    const monthsBack = 11 - m;
    for (let k = 0; k < 150; k++) {
      const dayInMonth = randBetween(1, 28);
      const dayOffset = monthsBack * 30 + (30 - dayInMonth);
      const scheduledPickup = dateDaysAgo(dayOffset);
      const mode = pickW(MODE_WEIGHTS);
      const lane = pickW(LANES.map(l => [l, l.weight]));
      const [scac, carrierName, onTimeRate] = CARRIERS[randBetween(0, CARRIERS.length - 1)];

      // On-time modeling: probability from carrier base rate plus mode adjustment
      const modeOnTimeAdj = { OTR: 0, LTL: -0.03, Drayage: -0.05, Ocean: -0.08, Intermodal: -0.04, Air: 0.04, Parcel: 0.02 }[mode] || 0;
      const pOnTime = Math.max(0.5, Math.min(0.99, onTimeRate + modeOnTimeAdj));
      const isOnTime = rnd() < pOnTime;

      const transitDays = { OTR: 2, LTL: 3, Drayage: 1, Ocean: 14, Intermodal: 5, Air: 1, Parcel: 2 }[mode] || 2;
      const scheduledDeliveryDate = new Date(scheduledPickup);
      scheduledDeliveryDate.setDate(scheduledDeliveryDate.getDate() + transitDays);

      // Pickup variance: small if on-time, larger if late
      const pickupVarHrs = isOnTime ? randFloat(-1, 1, 1) : randFloat(2, 8, 1);
      const deliveryVarHrs = isOnTime ? randFloat(-1, 1, 1) : randFloat(3, 24, 1);
      const actualPickupDate = addHours(scheduledPickup.toISOString(), pickupVarHrs);
      const actualDeliveryDate = addHours(scheduledDeliveryDate.toISOString(), deliveryVarHrs);

      // Rates — per-mode base × miles × quarterly drift × seasonal spike
      const basePerMile = { OTR: 2.25, LTL: 1.05, Drayage: 3.80, Ocean: 0.42, Intermodal: 1.65, Air: 9.50, Parcel: 1.80 }[mode] || 2.0;
      const rateMult = rateMultiplier(scheduledPickup);
      const miles = lane.miles + randBetween(-30, 30);
      const sellRate = parseFloat((basePerMile * miles * rateMult).toFixed(2));
      const marginPct = randFloat(0.08, 0.22, 3);
      const buyRate = parseFloat((sellRate * (1 - marginPct)).toFixed(2));
      const margin = parseFloat((sellRate - buyRate).toFixed(2));

      // Customer satisfaction: weighted to 4-5, some 2-3
      const csat = pickW([[5, 50], [4, 30], [3, 12], [2, 6], [1, 2]]);

      const id = `hist-load-${pad(idCounter++, 6)}`;
      const loadNumber = `WC-H${pad(idCounter, 6)}`;
      const customerId = pick(CUSTOMER_IDS);

      out.push({
        id,
        loadNumber,
        mode,
        status: 'delivered',
        originCity: lane.from,
        destCity: lane.to,
        miles,
        carrierScac: scac,
        carrierName,
        scheduledPickupDate: scheduledPickup.toISOString(),
        scheduledDeliveryDate: scheduledDeliveryDate.toISOString(),
        actualPickupDate,
        actualDeliveryDate,
        onTime: isOnTime,
        buyRate,
        sellRate,
        margin,
        marginPct,
        customerSatisfactionScore: csat,
        historicalYear: scheduledPickup.getFullYear(),
        historicalMonth: scheduledPickup.getMonth() + 1,
        ...uf({ customerId, loadId: id }, scheduledPickup.toISOString()),
        updatedAt: actualDeliveryDate,
      });
    }
  }
  return out;
}

// ═══════════════════════════════════════════════════════════════════
// HISTORICAL EXCEPTIONS (300 records)
// ═══════════════════════════════════════════════════════════════════
const EXCEPTION_TYPES = [
  ['late_delivery',   35],
  ['late_pickup',     30],
  ['temp_breach',     20],
  ['carrier_no_show', 10],
  ['damage',           5],
];

export function generateHistoricalExceptions(historicalLoads, seed = 18002) {
  const { pickW, randBetween, randFloat, pad, rnd } = makeHelpers(prng(seed));
  const out = [];
  const lateLoads = historicalLoads.filter(l => !l.onTime);
  // 25 per month × 12 = 300; link ~70% to late loads, rest random
  let idCounter = 200000;
  for (let m = 0; m < 12; m++) {
    for (let k = 0; k < 25; k++) {
      const type = pickW(EXCEPTION_TYPES);
      // Prefer a late load when the exception is delivery/pickup related
      const useLate = ['late_delivery', 'late_pickup'].includes(type) && lateLoads.length > 0 && rnd() < 0.85;
      const load = useLate ? lateLoads[randBetween(0, lateLoads.length - 1)] : historicalLoads[randBetween(0, historicalLoads.length - 1)];

      const createdAt = load.scheduledPickupDate;
      // Resolution: mean 2.4h with exponential-ish tail
      const hoursToResolve = Math.max(0.25, randFloat(0.25, 8, 2) * (rnd() < 0.8 ? 1 : 2.5));
      const resolvedAt = addHours(createdAt, hoursToResolve);

      const id = `hist-exc-${pad(idCounter++, 6)}`;
      out.push({
        id,
        type,
        severity: type === 'damage' || type === 'temp_breach' ? 'high' : 'medium',
        status: 'resolved',
        description: `${type.replace(/_/g, ' ')} on ${load.loadNumber} — ${load.carrierName}`,
        loadId: load.id,
        loadNumber: load.loadNumber,
        carrierScac: load.carrierScac,
        carrierName: load.carrierName,
        mode: load.mode,
        originCity: load.originCity,
        destCity: load.destCity,
        resolvedAt,
        resolutionHours: parseFloat(hoursToResolve.toFixed(2)),
        resolutionNotes: 'Resolved per standard exception playbook',
        ...uf({ customerId: load.customerId, loadId: load.id }, createdAt),
        updatedAt: resolvedAt,
      });
    }
  }
  return out;
}

// ═══════════════════════════════════════════════════════════════════
// INVENTORY DEMAND (20 SKUs × 365 days = 7,300 records)
// ═══════════════════════════════════════════════════════════════════
const SKUS = [
  // Fast movers (5)
  { sku: 'SKU-SEAFOOD-SALMON-01', category: 'seafood', tier: 'fast',   baseDaily: 180 },
  { sku: 'SKU-BEEF-RIBEYE-01',    category: 'beef',    tier: 'fast',   baseDaily: 160 },
  { sku: 'SKU-POULTRY-CHBRST-01', category: 'poultry', tier: 'fast',   baseDaily: 220 },
  { sku: 'SKU-PRODUCE-APPLES-01', category: 'produce', tier: 'fast',   baseDaily: 200 },
  { sku: 'SKU-SEAFOOD-SHRIMP-01', category: 'seafood', tier: 'fast',   baseDaily: 140 },
  // Medium movers (10)
  { sku: 'SKU-BEEF-GROUND-01',    category: 'beef',    tier: 'medium', baseDaily: 85 },
  { sku: 'SKU-BEEF-STRIP-01',     category: 'beef',    tier: 'medium', baseDaily: 75 },
  { sku: 'SKU-POULTRY-WINGS-01',  category: 'poultry', tier: 'medium', baseDaily: 90 },
  { sku: 'SKU-POULTRY-THIGH-01',  category: 'poultry', tier: 'medium', baseDaily: 80 },
  { sku: 'SKU-SEAFOOD-TUNA-01',   category: 'seafood', tier: 'medium', baseDaily: 65 },
  { sku: 'SKU-SEAFOOD-COD-01',    category: 'seafood', tier: 'medium', baseDaily: 60 },
  { sku: 'SKU-PRODUCE-LETTUCE-01',category: 'produce', tier: 'medium', baseDaily: 95 },
  { sku: 'SKU-PRODUCE-TOMATO-01', category: 'produce', tier: 'medium', baseDaily: 85 },
  { sku: 'SKU-BEEF-BRISKET-01',   category: 'beef',    tier: 'medium', baseDaily: 55 },
  { sku: 'SKU-POULTRY-WHOLE-01',  category: 'poultry', tier: 'medium', baseDaily: 70 },
  // Slow movers (5)
  { sku: 'SKU-SEAFOOD-LOBSTER-01',category: 'seafood', tier: 'slow',   baseDaily: 20 },
  { sku: 'SKU-BEEF-WAGYU-01',     category: 'beef',    tier: 'slow',   baseDaily: 15 },
  { sku: 'SKU-SEAFOOD-CRAB-01',   category: 'seafood', tier: 'slow',   baseDaily: 18 },
  { sku: 'SKU-POULTRY-DUCK-01',   category: 'poultry', tier: 'slow',   baseDaily: 22 },
  { sku: 'SKU-PRODUCE-TRUFFLE-01',category: 'produce', tier: 'slow',   baseDaily: 8  },
];

export function generateHistoricalDemand(seed = 18003) {
  const { randFloat, pad, rnd } = makeHelpers(prng(seed));
  const out = [];
  let idCounter = 300000;

  // Pre-pick 8 stockout positions spread across time+skus
  const stockoutKeys = new Set();
  const rndLocal = prng(seed + 1000);
  while (stockoutKeys.size < 8) {
    const day = Math.floor(rndLocal() * 365);
    const s = Math.floor(rndLocal() * SKUS.length);
    stockoutKeys.add(`${day}|${s}`);
  }

  for (let d = 0; d < 365; d++) {
    const daysBack = 364 - d;
    const date = dateDaysAgo(daysBack);
    const seasonalDate = date;
    const weeklyMul = weeklyMultiplier(date);

    SKUS.forEach((sku, sIdx) => {
      const seasonalMul = seasonalMultiplier(seasonalDate, sku.category);
      const noise = 1 + (rnd() - 0.5) * 0.20; // ±10%
      const predicted = sku.baseDaily * seasonalMul * weeklyMul;
      const actual = Math.max(0, Math.round(predicted * noise));

      const isStockout = stockoutKeys.has(`${d}|${sIdx}`);
      const unitsSold = isStockout ? Math.max(0, actual - Math.round(actual * randFloat(0.3, 0.6, 2))) : actual;
      const unitsShort = actual - unitsSold;

      const id = `hist-dem-${pad(idCounter++, 6)}`;
      out.push({
        id,
        sku: sku.sku,
        category: sku.category,
        velocityTier: sku.tier,
        date: date.toISOString().slice(0, 10), // YYYY-MM-DD
        forecastDemand: Math.round(predicted),
        actualDemand: actual,
        unitsSold,
        unitsShort,
        stockoutEvent: isStockout,
        dayOfWeek: date.getDay(),
        month: date.getMonth() + 1,
        ...uf({}, date.toISOString()),
      });
    });
  }
  return out;
}

// ═══════════════════════════════════════════════════════════════════
// CHECK CALLS (500 records)
// ═══════════════════════════════════════════════════════════════════
export function generateHistoricalCheckCalls(historicalLoads, seed = 18004) {
  const { pickW, randBetween, randFloat, pad, rnd } = makeHelpers(prng(seed));
  const out = [];
  let idCounter = 400000;
  const sample = historicalLoads.slice().sort(() => rnd() - 0.5).slice(0, 500);

  for (const load of sample) {
    const carrier = CARRIERS.find(c => c[0] === load.carrierScac) || CARRIERS[0];
    const answerRate = carrier[3];
    // Outcome: 75% confirmed on time, 15% delay, 10% no-answer — weight by
    // carrier's answer rate (lower answer rate → more no-answers)
    const noAnswerP = Math.max(0.02, 0.10 + (0.92 - answerRate) * 0.5);
    const outcome = rnd() < noAnswerP
      ? 'no_answer'
      : rnd() < 0.85
        ? 'confirmed_on_time'
        : 'delay_reported';

    const calledAt = addHours(load.scheduledPickupDate, randFloat(-2, 6, 1));
    const id = `hist-call-${pad(idCounter++, 6)}`;

    out.push({
      id,
      loadId: load.id,
      loadNumber: load.loadNumber,
      carrierScac: load.carrierScac,
      carrierName: load.carrierName,
      calledAt,
      outcome,
      notes: outcome === 'confirmed_on_time'
        ? 'Driver confirmed ETA within window'
        : outcome === 'delay_reported'
          ? `Driver reported delay — ${randBetween(1, 6)}h behind schedule`
          : 'No answer — voicemail left',
      durationSeconds: outcome === 'no_answer' ? 0 : randBetween(30, 420),
      answeredBy: outcome === 'no_answer' ? null : 'driver',
      ...uf({ customerId: load.customerId, loadId: load.id }, calledAt),
    });
  }
  return out;
}

// ═══════════════════════════════════════════════════════════════════
// WMS RECEIVING (3-5 STOs per day × 365)
// ═══════════════════════════════════════════════════════════════════
export function generateHistoricalReceiving(seed = 18005) {
  const { pick, randBetween, randFloat, pad } = makeHelpers(prng(seed));
  const out = [];
  let idCounter = 500000;

  for (let d = 0; d < 365; d++) {
    const daysBack = 364 - d;
    const date = dateDaysAgo(daysBack);
    const stos = randBetween(3, 5);
    for (let k = 0; k < stos; k++) {
      const receivedAt = addHours(date.toISOString(), randBetween(6, 18));
      const expectedUnits = randBetween(80, 800);
      const receivedUnits = Math.round(expectedUnits * randFloat(0.95, 1.00, 3));
      const id = `hist-rcv-${pad(idCounter++, 6)}`;
      out.push({
        id,
        stoNumber: `STO-${pad(900000 + idCounter, 7)}`,
        receivedAt,
        carrierScac: pick(CARRIERS)[0],
        dockDoor: `D${randBetween(1, 24)}`,
        expectedUnits,
        receivedUnits,
        unitsShort: expectedUnits - receivedUnits,
        status: 'received',
        warehouseId: pick(['DAL-01', 'MEM-01', 'CHI-01', 'LAX-01']),
        skuCount: randBetween(3, 18),
        processingMinutes: randBetween(15, 95),
        ...uf({}, receivedAt),
      });
    }
  }
  return out;
}

// ═══════════════════════════════════════════════════════════════════
// WMS PUTAWAY (linked 1:1 to receiving)
// ═══════════════════════════════════════════════════════════════════
export function generateHistoricalPutaway(receivingRecords, seed = 18006) {
  const { pick, randBetween, pad } = makeHelpers(prng(seed));
  const out = [];
  let idCounter = 600000;

  for (const rcv of receivingRecords) {
    const putawayAt = addHours(rcv.receivedAt, randBetween(1, 8));
    const id = `hist-put-${pad(idCounter++, 6)}`;
    out.push({
      id,
      receivingId: rcv.id,
      stoNumber: rcv.stoNumber,
      putawayAt,
      warehouseId: rcv.warehouseId,
      unitsPutaway: rcv.receivedUnits,
      locations: randBetween(1, 6),
      zone: pick(['A', 'B', 'C', 'D', 'E']),
      laborer: `L${randBetween(1, 24)}`,
      durationMinutes: randBetween(12, 75),
      status: 'complete',
      ...uf({}, putawayAt),
    });
  }
  return out;
}

// ═══════════════════════════════════════════════════════════════════
// WMS PICKS (50-80 per day for last 90 days = ~5,850)
// Older months: daily aggregate summary records.
// ═══════════════════════════════════════════════════════════════════
export function generateHistoricalPicks(seed = 18007) {
  const { pick, randBetween, randFloat, pad, rnd } = makeHelpers(prng(seed));
  const out = [];
  let idCounter = 700000;

  // Detailed picks: last 90 days
  for (let d = 0; d < 90; d++) {
    const daysBack = 89 - d;
    const date = dateDaysAgo(daysBack);
    const dayPicks = randBetween(50, 80);
    for (let k = 0; k < dayPicks; k++) {
      const pickedAt = addHours(date.toISOString(), randFloat(6, 22, 2));
      const sku = SKUS[randBetween(0, SKUS.length - 1)];
      const id = `hist-pik-${pad(idCounter++, 7)}`;
      out.push({
        id,
        sku: sku.sku,
        category: sku.category,
        pickedAt,
        pickerId: `P${randBetween(1, 18)}`,
        zone: pick(['A', 'B', 'C', 'D', 'E']),
        unitsPicked: randBetween(1, 24),
        orderNumber: `ORD-${pad(idCounter, 7)}`,
        warehouseId: pick(['DAL-01', 'MEM-01', 'CHI-01', 'LAX-01']),
        durationSeconds: randBetween(30, 240),
        accuracy: rnd() < 0.98 ? 1.0 : 0.0,
        recordType: 'detail',
        ...uf({}, pickedAt),
      });
    }
  }

  // Daily aggregates: days 90-364 (9 months)
  for (let d = 90; d < 365; d++) {
    const daysBack = 364 - d;
    const date = dateDaysAgo(daysBack);
    const totalPicks = randBetween(50, 80) * (0.85 + rnd() * 0.3);
    const id = `hist-pik-agg-${pad(idCounter++, 7)}`;
    out.push({
      id,
      date: date.toISOString().slice(0, 10),
      warehouseId: 'ALL',
      recordType: 'daily_aggregate',
      totalPicks: Math.round(totalPicks),
      totalOrders: Math.round(totalPicks * randFloat(0.4, 0.6, 2)),
      avgAccuracy: randFloat(0.97, 0.995, 4),
      avgDurationSeconds: randBetween(95, 145),
      ...uf({}, date.toISOString()),
    });
  }

  return out;
}

// ═══════════════════════════════════════════════════════════════════
// WMS LABOR PRODUCTIVITY (3 shifts × 365 days)
// ═══════════════════════════════════════════════════════════════════
export function generateHistoricalLaborProductivity(seed = 18008) {
  const { pick, randBetween, randFloat, pad } = makeHelpers(prng(seed));
  const out = [];
  let idCounter = 800000;
  const shifts = [
    { code: 'day',     start: 6,  end: 14 },
    { code: 'evening', start: 14, end: 22 },
    { code: 'night',   start: 22, end: 30 }, // 30 = 6 next day
  ];

  for (let d = 0; d < 365; d++) {
    const daysBack = 364 - d;
    const date = dateDaysAgo(daysBack);
    for (const shift of shifts) {
      const headcount = randBetween(6, 24);
      const productivity = randFloat(45, 92, 1); // picks per labor-hour
      const hoursWorked = headcount * 8;
      const id = `hist-lab-${pad(idCounter++, 6)}`;
      out.push({
        id,
        date: date.toISOString().slice(0, 10),
        shift: shift.code,
        warehouseId: pick(['DAL-01', 'MEM-01', 'CHI-01', 'LAX-01']),
        headcount,
        hoursWorked,
        picksPerHour: productivity,
        totalPicks: Math.round(hoursWorked * productivity),
        overtimeHours: randBetween(0, 12),
        absenteeism: randFloat(0, 0.15, 3),
        ...uf({}, date.toISOString()),
      });
    }
  }
  return out;
}

// ═══════════════════════════════════════════════════════════════════
// TOP-LEVEL ORCHESTRATOR
// ═══════════════════════════════════════════════════════════════════
export function generateAllHistorical() {
  const loads     = generateHistoricalLoads();
  const exceptions = generateHistoricalExceptions(loads);
  const demand    = generateHistoricalDemand();
  const checkCalls = generateHistoricalCheckCalls(loads);
  const receiving = generateHistoricalReceiving();
  const putaway   = generateHistoricalPutaway(receiving);
  const picks     = generateHistoricalPicks();
  const labor     = generateHistoricalLaborProductivity();

  return {
    historical_loads:       loads,
    historical_exceptions:  exceptions,
    historical_demand:      demand,
    historical_check_calls: checkCalls,
    wms_receiving:          receiving,
    wms_putaway:            putaway,
    wms_picks:              picks,
    wms_labor:              labor,
  };
}

export const HISTORICAL_SUMMARY = {
  tenantId: T,
  targetVolumes: {
    historical_loads:       1800,
    historical_exceptions:   300,
    historical_demand:      7300,
    historical_check_calls:  500,
    wms_receiving:         '~1460',
    wms_putaway:           '~1460',
    wms_picks:             '~5850 detailed + 275 daily aggregates',
    wms_labor:              1095,
  },
};
